import React from "react";
import { useQuery } from "@apollo/client";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { Skeleton } from "@mui/material";

import { useUser } from "src/hooks/useUser";

import SchemaHeader from "src/components/index/schemas/SchemaHeader";
import SubjectBody from "src/components/index/schemas/SubjectBody";
import { socialMetadata } from "src/components/layout/Head";

import { propertyQuery, subjectQuery } from "src/lib/graphql/schemas.queries";
import {
  PropertyQuery,
  PropertyQueryVariables,
  SubjectQuery,
  SubjectQueryVariables,
} from "src/lib/graphql/types";
import { schemaPath } from "src/lib/schemaOrgUrl";
import NotFound from "src/pages/404";
import SchemaMeta from "src/components/index/schemas/SchemaMeta";
import SchemaChildren from "src/components/index/schemas/SchemaChildren";
import PropertyBody from "src/components/index/schemas/PropertyBody";

const isSubjectResult = (
  obj: SubjectQuery["subject"] | PropertyQuery["property"]
): obj is SubjectQuery["subject"] => obj.__typename === "Subject";

const SchemaPage: NextPage = () => {
  const router = useRouter();
  const [user] = useUser();

  const name = router.query.name! as string;
  const namespace = router.query.shortname as string;

  const isSubject = name[0].toUpperCase() === name[0];

  const { loading, data, refetch } = useQuery<
    SubjectQuery | PropertyQuery,
    SubjectQueryVariables | PropertyQueryVariables
  >(isSubject ? subjectQuery : propertyQuery, {
    variables: { name, namespace },
  });
  const entity = isSubject
    ? (data as SubjectQuery)?.subject
    : (data as PropertyQuery)?.property;

  useEffect(() => {
    refetch();
  }, [refetch, user?.id]);

  if (!entity) {
    if (loading) {
      return (
        <>
          <div className="project-header">
            <div className="container project-header-content schema-header">
              <div className="schema-breadcrumbs listing-category">
                <Skeleton style={{ width: "40%" }} />
              </div>
              <div className="header-flex-container">
                <div style={{ width: "100%" }} className="schema-summary">
                  <h3 className="project-title heading-3">
                    <Skeleton style={{ width: "30%" }} />
                  </h3>

                  <p className="schema-description">
                    <Skeleton style={{ width: "60%" }} />
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="container project-main-content">
            <div className="schema-property-tables">
              <Skeleton style={{ marginRight: 50, height: 500 }} />
            </div>
            <div className="project-metadata small-text">
              <Skeleton style={{ height: 500 }} />
            </div>
          </div>
        </>
      );
    } else {
      return <NotFound />;
    }
  }

  return (
    <>
      <Head>
        <link rel="canonical" href={`https://hash.ai${schemaPath(entity)}`} />
        {socialMetadata({
          title: `${entity.name} - HASH`,
          url: `https://hash.ai${schemaPath(entity)}`,
          description: entity.description,
        })}
      </Head>

      <SchemaHeader isProperty={!isSubject} schema={entity} />

      <div className="container project-main-content">
        {isSubjectResult(entity) ? (
          <SubjectBody schema={entity} />
        ) : (
          <PropertyBody property={entity} />
        )}
        <div className="project-metadata small-text">
          <SchemaMeta schema={entity} />
          <SchemaChildren
            children={
              isSubjectResult(entity) ? entity.children : entity.subProperties
            }
            isProperty={!isSubject}
          />
        </div>
      </div>
    </>
  );
};

const SchemaPageWrapper: NextPage = (props) => (
  <div className="schema-page project-page">
    <SchemaPage {...props} />
  </div>
);

export default SchemaPageWrapper;
